import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import chroma from 'chroma-js';
import { media } from '~/styles';
import { apiDelete } from '~/common';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import AssetPickerTypes, { NonAssetPickerTypes } from '~/components/common/asset-picker/asset-picker-types';
import { HubLearnerSearchBar } from '~/clc/features/clc-hub-learner-search/components/HubLearnerSearchBar';
import academyStyles from './academy-styles';
import AcademyUserAvatar from './academy-user-avatar';
import AcademySearchBar from './academy-search-bar';

const DEFAULT_CUSTOM_LOGO_HEIGHT = 30;

const LEARNER_PROFILE_URL = '/profile/';
const LEARNER_MY_TRAININGS_URL = '/profile/my_trainings/';
const LEARNER_MY_ORDERS_URL = '/profile/orders/';
const LEARNER_LOGOUT_URL = '/logout';

export default class AcademyThemeNavigation extends Component {
  TRANSLATED_LOGO_LABEL = I18n.t('js.academy.navigation.logo_label');

  TRANSLATED_LOGIN = I18n.t('js.academy.navigation.login');

  TRANSLATED_OPENS_NEW_WINDOW = I18n.t('js.academy.navigation.opens_new_window');

  TRANSLATED_PROFILE = I18n.t('js.academy.navigation.profile');

  TRANSLATED_MY_TRAININGS =
    this.props.academy.themeOptions.contentLabelOverrides.my_trainings || I18n.t('common.academy.content.my_trainings');

  TRANSLATED_ORDER_HISTORY = I18n.t('js.academy.navigation.order_history');

  TRANSLATED_LOGOUT = I18n.t('js.academy.navigation.logout');

  constructor(props) {
    super(props);

    this.state = {
      mobileNavVisible: false,
      searchVisible: false,
    };
  }

  render() {
    const {
      academy,
      academyContentTypesToLocalizedString,
      backToLink,
      backToLinkText,
      certificationsEnabled,
      communityContentTypesToLocalizedString,
      contentWrapperStyle,
      eventsEnabled,
      headerLinks,
      homepageLink,
      linkColor,
      linkHoverColor,
      logo,
      logoHeight,
      noBannerContent,
      pathsEnabled,
      showBackToLink,
      showSearch,
    } = this.props;

    const navigationClasses = this.getNavigationClasses();
    const navigationLinks = _.map(headerLinks, (headerLink) => this.renderNavigationLinks(headerLink));
    const mobileLoginLink = this.renderMobileLoginLink();
    if (mobileLoginLink) navigationLinks.push(mobileLoginLink);
    const validSearchTypes = [
      NonAssetPickerTypes.ACADEMY_COLLECTION_CONTENT,
      NonAssetPickerTypes.ACADEMY_CERTIFICATION_CONTENT,
      NonAssetPickerTypes.ACADEMY_PATH_CONTENT,
      AssetPickerTypes.ACADEMY_COLLECTION,
    ];

    if (certificationsEnabled || pathsEnabled) {
      validSearchTypes.push(AssetPickerTypes.ACADEMY_CERTIFICATION);
      validSearchTypes.push(AssetPickerTypes.ACADEMY_PATH);
    }
    if (eventsEnabled) {
      validSearchTypes.push(AssetPickerTypes.ACADEMY_EVENT);
      validSearchTypes.push(NonAssetPickerTypes.ACADEMY_EVENT_SESSION);
      validSearchTypes.push(NonAssetPickerTypes.ACADEMY_EVENT_ON_DEMAND_CONTENT);
    }

    return (
      <AcademyNavigation
        className={navigationClasses.join(' ')}
        linkColor={linkColor}
        linkHoverColor={linkHoverColor}
        mobileNavVisible={this.state.mobileNavVisible}
        noBannerContent={noBannerContent}
        searchVisible={this.state.searchVisible}
        isLoggedIn={Boolean(getExternalUser())}
      >
        <AcademyNavigationContentWrapper
          className={`academy-navigation-content-wrapper ${academyCustomCSS.header.navigation}`}
          hasMobileNav={!_.isEmpty(navigationLinks)}
          style={contentWrapperStyle || {}}
        >
          {logo && (
            <a href={homepageLink || '/'} className={`academy-logo-link ${academyCustomCSS.header.logoLink}`}>
              <CustomLogo
                className="academy-logo"
                logoHeight={logoHeight || DEFAULT_CUSTOM_LOGO_HEIGHT}
                src={logo}
                alt={academy?.name || this.TRANSLATED_LOGO_LABEL}
              />
            </a>
          )}
          {!_.isEmpty(navigationLinks) && (
            <nav
              className={`academy-nav ${
                this.state.mobileNavVisible
                  ? academyCustomCSS.header.menuBackdropMobile
                  : academyCustomCSS.header.customLinks
              }`}
            >
              <ul>{navigationLinks}</ul>
            </nav>
          )}
          {showBackToLink ? (
            <BackToLink
              className={academyCustomCSS.header.goBackToLink}
              href={backToLink}
              target="_blank"
              linkColor={linkColor}
              linkHoverColor={linkHoverColor}
            >
              <span>{backToLinkText}</span>
              <i className="fas fa-external-link-alt" aria-label={this.TRANSLATED_OPENS_NEW_WINDOW} />
            </BackToLink>
          ) : (
            <BackToLinkSpacer />
          )}
          {showSearch && (
            <SearchBarWrapper searchVisible={this.state.searchVisible}>
              <i
                className={`fas fa-search ${academyCustomCSS.header.searchBarIconMobile}`}
                onClick={this.showMobileSearchBar}
              />
              <i
                className={`fas fa-times ${academyCustomCSS.header.searchBarCloseIconMobile}`}
                onClick={this.hideMobileSearchBar}
              />
              <HubLearnerSearchBar
                className={`academy-search-bar-wrapper ${academyCustomCSS.header.searchBar}`}
                algoliaAppId={window.ENV_VARS.ALGOLIA_APP_ID}
                algoliaSearchApiKey={window.ALGOLIA_SEARCH_KEY}
                academyContentTypesToLocalizedString={academyContentTypesToLocalizedString}
                communityContentTypesToLocalizedString={communityContentTypesToLocalizedString}
                prioritizeAcademies
              />
            </SearchBarWrapper>
          )}
          {this.renderLoginButton()}
          {this.renderProfileLink()}
          {!_.isEmpty(navigationLinks) && (
            <i
              className={`fas fa-bars ${academyCustomCSS.header.menuButtonMobile}`}
              onClick={() => this.setState({ mobileNavVisible: true })}
            />
          )}
          {!_.isEmpty(navigationLinks) && (
            <i
              className={`fas fa-times ${academyCustomCSS.header.closeMenuButtonMobile}`}
              onClick={() => this.setState({ mobileNavVisible: false })}
            />
          )}
        </AcademyNavigationContentWrapper>
      </AcademyNavigation>
    );
  }

  showMobileSearchBar = () => {
    this.setState({
      searchVisible: true,
    });
    document.body.style.overflow = 'hidden';
  };

  hideMobileSearchBar = () => {
    this.setState({
      searchVisible: false,
    });
    document.body.style.overflow = 'auto';
  };

  renderLoginButton = () => {
    if (!getExternalUser()) {
      return (
        <LogOutButton
          className={`logout-button ${academyCustomCSS.header.loginButton}`}
          color={this.props.linkColor}
          hoverColor={this.props.linkHoverColor}
          href="/login"
        >
          {this.TRANSLATED_LOGIN}
        </LogOutButton>
      );
    }
  };

  renderMobileLoginLink = () => {
    if (!getExternalUser()) {
      return (
        <li id="login" key="mobile-login" className={`mobile-login-link ${academyCustomCSS.header.loginLinkMobile}`}>
          <a href="/login">{this.TRANSLATED_LOGIN}</a>
        </li>
      );
    }
  };

  renderProfileLink = () => {
    const externalUser = getExternalUser();
    const { academy, linkColor } = this.props;
    if (!externalUser) return '';

    const subMenu = [
      {
        linkText: this.TRANSLATED_PROFILE,
        clickHandler: this.goToProfile,
        href: LEARNER_PROFILE_URL,
      },
      {
        linkText: this.TRANSLATED_MY_TRAININGS,
        clickHandler: this.goToMyTrainings,
        badgeCount: isFeatureEnabled('academies_assignments') ? externalUser.outstandingAssignmentsCount : undefined,
        href: LEARNER_MY_TRAININGS_URL,
      },
    ];

    if (isFeatureEnabled('ecommerce') && academy.isEcommerceSetUp) {
      subMenu.push({
        linkText: this.TRANSLATED_ORDER_HISTORY,
        clickHandler: this.goToOrderHistory,
        href: LEARNER_MY_ORDERS_URL,
      });
    }

    subMenu.push({
      linkText: this.TRANSLATED_LOGOUT,
      clickHandler: this.logoutCurrentUser,
      href: LEARNER_LOGOUT_URL,
    });

    return (
      <AcademyUserAvatar
        backgroundColor="transparent"
        className="academy-navigation-user-avatar"
        imageSrc={externalUser.avatar || ''}
        name={externalUser.displayName}
        subMenu={subMenu}
        subMenuWidth={135}
        width={40}
        textColor={linkColor}
        textHoverColor={linkColor}
        subnavActiveLinkColor={this.props.subnavActiveLinkColor}
      />
    );
  };

  logoutCurrentUser = () => {
    if (getUrlParameters().prevent_default !== 'true') {
      apiDelete(
        'logout',
        null,
        (resp) => {
          window.location.href = resp;
        },
        (_resp) => window.location.reload(),
        '/',
      );
    }
  };

  goToProfile = () => {
    if (getUrlParameters().prevent_default !== 'true') {
      window.location.href = LEARNER_PROFILE_URL;
    }
  };

  goToMyTrainings = () => {
    if (getUrlParameters().prevent_default !== 'true') {
      window.location.href = LEARNER_MY_TRAININGS_URL;
    }
  };

  goToOrderHistory = () => {
    if (getUrlParameters().prevent_default !== 'true') {
      window.location.href = LEARNER_MY_ORDERS_URL;
    }
  };

  getNavigationClasses = () => {
    const navigationClasses = ['academy-navigation'];
    if (!this.props.logo) navigationClasses.push('no-custom-logo');

    return navigationClasses;
  };

  renderNavigationLinks = (link) => (
    <li
      id={`${link.linkText}-${link.link}`}
      key={`${link.linkText}-${link.link}`}
      className={academyCustomCSS.header.customLink}
    >
      <a
        href={link.link}
        target={link.shouldOpenInNewWindow || _.isUndefined(link.shouldOpenInNewWindow) ? '_blank' : '_self'}
        rel="noreferrer"
      >
        {link.linkText}
      </a>
    </li>
  );
}

const AcademyNavigation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 8rem);
  margin: 0 4rem;
  ${media.mobile} {
    margin-left: 2rem;
    margin-right: 2rem;
    width: calc(100% - 4rem);
  }
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  height: ${academyStyles.header.headerHeight}px;
  ${(props) => props.noBannerContent && `height: ${academyStyles.header.headerShorterHeight}px;`}
  z-index: 15;
  .academy-navigation-content-wrapper {
    width: 100%;
    max-width: ${academyStyles.common.maxWidth}px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    padding-top: ${academyStyles.header.headerHeight / 2 - DEFAULT_CUSTOM_LOGO_HEIGHT / 2}px;
  }
  img {
    height: ${DEFAULT_CUSTOM_LOGO_HEIGHT}px;
    width: auto;
    margin-right: 75px;
  }
  a.academy-logo-link {
    img {
      float: left;
      color: ${(props) => props.linkColor} !important;
    }
    &:after {
      content: '';
      display: table;
      border-collapse: collapse;
      clear: both;
    }
  }
  ul {
    padding: 0;
    list-style: none;
    list-style-type: none;
    list-style-image: none;
    li {
      display: inline;
      &.mobile-login-link {
        display: none;
      }
      a {
        display: inline-block;
        margin: 0 20px;
        transition: all 0.3s ease-in-out;
        font-size: 1rem;
        color: ${(props) => props.linkColor};
        &:hover,
        &.active {
          text-decoration: none;
          color: ${(props) => props.linkHoverColor};
        }
      }
    }
  }

  &.no-custom-logo {
    ul {
      li:first-child {
        a {
          margin-left: 0;
        }
      }
    }
  }

  .academy-navigation-user-avatar {
    margin-left: 15px;
    ${(props) => props.searchVisible && ' display: none;'};
  }

  .fa-bars,
  .fa-times {
    display: none;
    color: #000;
    cursor: pointer;
    width: 40px;
    height: 40px;
    background-color: #fff;
    box-shadow: 0 3px 25px rgba(161, 179, 200, 0.23);
    position: fixed;
    line-height: 40px;
    text-align: center;
    right: 2rem;
    top: 35px;
    z-index: 15;

    ${(props) => props.isLoggedIn && 'top: 40px;'};
  }

  ${media.mobile} {
    z-index: 99999;
    img {
      margin-right: 0;
      height: ${DEFAULT_CUSTOM_LOGO_HEIGHT}px !important;
    }
    .fa-bars {
      display: ${(props) => (props.mobileNavVisible ? 'none' : 'block')};
      top: ${(props) => props.searchVisible && '30px'};
    }
    .fa-times {
      display: ${(props) => (props.mobileNavVisible ? 'block' : 'none')};
      ${(props) =>
        props.searchVisible &&
        `
        top: 30px;
        background-color: transparent;
        box-shadow: none;
      `};
    }
    .academy-nav {
      position: fixed;
      top: 0;
      left: 100%;
      ${(props) => props.mobileNavVisible && 'left: 0;'}
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.75);
      transition: all 0.3s ease-in-out;
      z-index: 10;
      ul {
        position: absolute;
        top: ${academyStyles.header.headerHeight}px;
        width: 100%;
        li {
          display: block;
          a {
            font-size: 1.5rem;
            display: block;
            text-align: right;
            margin: 0 2rem;
            /* If the contrast between the black backdrop
             and the link color is below the recommended 4.5,
             we fallback to white so the links are still readable.
            */
            color: ${(props) => (chroma.contrast('#000', props.linkColor) > 4.5 ? props.linkColor : 'white')};
          }
          cursor: pointer;
        }
      }
    }
    .academy-navigation-content-wrapper {
      .logout-button {
        display: none;
      }
    }
  }

  @media print {
    display: none;
  }
`;

const AcademyNavigationContentWrapper = styled.div`
  ${media.mobile} {
    ${(props) => props.hasMobileNav && 'padding-right: 55px;'}
  }
`;

const CustomLogo = styled.img`
  height: ${(props) => props.logoHeight || DEFAULT_CUSTOM_LOGO_HEIGHT}px !important;
`;

const BackToLinkSpacer = styled.div`
  margin-left: auto;
`;

const BackToLink = styled.a`
  margin-left: auto;
  display: inline-block;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease-in-out;
  color: ${(props) => props.linkColor} !important;
  &:hover {
    text-decoration: none;
    color: ${(props) => props.linkHoverColor} !important;
  }
  i {
    margin-left: 5px;
  }
  ${media.mobile} {
    span {
      display: none;
    }
    i {
      margin: 0;
    }
  }
`;

const LogOutButton = styled.a`
  margin: 0 15px;
  border: solid 1px ${(props) => props.color};
  color: ${(props) => props.color} !important;
  border-radius: 7px;
  line-height: 2;
  padding: 0 25px;
  transition: all 0.3s ease-in-out;
  text-align: center;
  flex: 0 0 75px;
  &:hover {
    text-decoration: none;
    color: ${(props) => props.hoverColor} !important;
    border-color: ${(props) => props.hoverColor};
  }
`;

const SearchBarWrapper = styled.div`
  margin-left: 15px;
  .fa-search,
  .fa-times {
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
    display: none;
  }
  ${media.mobile} {
    .fa-search {
      display: inline-block;
    }
    ${(props) =>
      props.searchVisible &&
      `
        .fa-times {
          display: inline-block;
          z-index: 30;
          color: #000;
        }
        .fa-search {
          display: none;
        }
      `}
    .academy-search-bar-wrapper {
      display: none;
      position: absolute;
      top: 30px;
      left: -12px;
      width: calc(100% + 24px);
      z-index: 20;
      .dropdown-enabled-search-bar {
        background-color: #fff;
        height: 50px;
        border-radius: 25px;
        width: calc(100% - 65px);
        .search-bar {
          input {
            height: 48px;
          }
        }
        .search-bar-dropdown {
          width: calc(100% + 55px);
          top: 58px;
          left: 0;
          max-height: calc(100vh - 100px);
        }
      }
      ${(props) => props.searchVisible && 'display: block;'}
    }
  }
`;
